import * as React from "react";
import { Box, Button, Card, Radio, Stack, TextField, Typography } from "@mui/material";

import type { CatalogueVoice } from "@/hooks/api/use-voices";
import { VoiceSampleButton } from "@/components/voice-sample-button";

import { languageLabel } from "../constants";

/** What this language's agent will sound like: a curated voice, or an ask for a different one. */
export interface VoiceChoice {
	voiceId: string | null;
	/** "None of these", in the client's own words. Empty means no ask. */
	requestNote: string;
}

export interface VoicePickerProps {
	language: string;
	/** The active catalogue for this language, already filtered, in the admin's curation order. */
	voices: CatalogueVoice[];
	loading: boolean;
	/**
	 * The catalogue request failed. Distinct from an empty catalogue on purpose: "no voices yet"
	 * is a true statement about the product, and printing it during an outage tells the client
	 * something false about what we offer.
	 */
	failed?: boolean;
	value: VoiceChoice;
	readOnly: boolean;
	onChange: (choice: VoiceChoice) => void;
}

/** The one muted line each state collapses to. */
function Label(): React.JSX.Element {
	return (
		<Typography variant="caption" color="text.secondary" sx={{ flex: "0 0 auto", width: 44 }}>
			Voice
		</Typography>
	);
}

/**
 * Choosing how this agent sounds.
 *
 * Cards with a play button rather than a dropdown of names, because the client is choosing how
 * their brand sounds to a stranger and no name conveys that — the sample does. The samples are
 * pre-recorded and curated; nothing is synthesised here.
 *
 * Closed by default and closed again the moment a voice is chosen. Step 2 already carries the
 * agent-kind choice, its prices and a call-flow upload for every language on the request, and five
 * voice cards per language opened by default would be the wall this redesign exists to remove. So
 * the resting state is one line: what was chosen, and a way to change it.
 *
 * "None of these" is a first-class option rather than a dead end. It records what the client
 * actually wants and raises the `voice_request` blocker, which is the one blocker that sits with
 * us rather than with them — nothing about it stops them quoting, confirming or carrying on.
 */
export function VoicePicker({
	language,
	voices,
	loading,
	failed,
	value,
	readOnly,
	onChange,
}: VoicePickerProps): React.JSX.Element {
	const [open, setOpen] = React.useState(false);
	const [askSelected, setAskSelected] = React.useState(false);

	const selected = voices.find((voice) => voice.id === value.voiceId) ?? null;
	const note = value.requestNote.trim();
	const asking = !value.voiceId && note.length > 0;

	/**
	 * Every resting state is the same row: the label, then one line beside it.
	 *
	 * Without this they were four different shapes — a bare sentence while loading, an indented row
	 * once loaded — so the step visibly re-laid itself out under the client's cursor as the
	 * catalogue arrived. The label is what makes any of these lines mean "voice" at all.
	 */
	const line = (text: React.ReactNode, color: string): React.JSX.Element => (
		<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
			<Label />
			<Typography variant="caption" color={color}>
				{text}
			</Typography>
		</Stack>
	);

	if (loading) return line("Loading…", "text.secondary");

	/**
	 * Nothing curated for this language yet, said once and got out of the way.
	 *
	 * Not a warning and not a blocker: a voice can be settled in preparation, and a client who
	 * cannot pick one has done nothing wrong. The screen shows what is.
	 *
	 * Only where there is nothing else to say. A request that already holds a choice or an ask
	 * falls through to the states below, which report both — an empty catalogue must not swallow
	 * something the client told us.
	 */
	if (failed && voices.length === 0 && !value.voiceId && !asking) {
		return line(
			<React.Fragment>
				We couldn&rsquo;t load the voices just now. You can pick one before this goes live.
			</React.Fragment>,
			"warning.main"
		);
	}

	if (voices.length === 0 && !value.voiceId && !asking) {
		return line(
			<React.Fragment>
				No {languageLabel(language)} voices on offer yet — we&rsquo;ll agree one with you in preparation.
			</React.Fragment>,
			"text.secondary"
		);
	}

	if (!open) {
		return (
			<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
				<Label />

				{selected ? <VoiceSampleButton sampleUrl={selected.sampleUrl} name={selected.displayName} /> : null}

				<Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
					{selected ? (
						<React.Fragment>
							<Typography variant="body2" noWrap>
								{selected.displayName}
							</Typography>
							{selected.persona ? (
								<Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
									{selected.persona}
								</Typography>
							) : null}
						</React.Fragment>
					) : value.voiceId ? (
						// The chosen voice has been retired since. Said plainly rather than rendered as a
						// blank, which would read as "never chose one" and lose what they told us.
						<Typography variant="body2" color="warning.main" noWrap>
							The voice chosen here is no longer offered.
						</Typography>
					) : asking ? (
						<React.Fragment>
							<Typography variant="body2" noWrap>
								You asked us for more voices
							</Typography>
							<Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
								{note}
							</Typography>
						</React.Fragment>
					) : (
						<Typography variant="body2" color="text.secondary">
							Not chosen yet
						</Typography>
					)}
				</Box>

				{readOnly ? null : (
					<Button
						size="small"
						sx={{ flex: "0 0 auto" }}
						onClick={() => {
							setAskSelected(asking);
							setOpen(true);
						}}
					>
						{selected || asking || value.voiceId ? "Change voice" : "Choose a voice"}
					</Button>
				)}
			</Stack>
		);
	}

	return (
		<Stack spacing={1}>
			<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
				<Label />
				<Typography variant="caption" color="text.secondary">
					{voices.length > 0 ? "Press play to hear each one." : "None on offer for this language yet."}
				</Typography>
			</Stack>

			<Box sx={{ display: "grid", gap: 1, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
				{voices.map((voice) => {
					const isSelected = value.voiceId === voice.id;
					const descriptors = [voice.persona, voice.gender].filter(Boolean).join(" · ");

					return (
						<Card
							key={voice.id}
							variant="outlined"
							sx={{ borderColor: isSelected ? "primary.main" : "divider", cursor: "pointer", p: 1 }}
							onClick={() => {
								setAskSelected(false);
								onChange({ voiceId: voice.id, requestNote: value.requestNote });
								setOpen(false);
							}}
						>
							<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
								<Radio checked={isSelected} size="small" sx={{ p: 0 }} />

								{/* Auditioning is not choosing: without this, pressing play would silently
								    select the card the button sits on. */}
								<Box sx={{ display: "flex" }} onClick={(event) => event.stopPropagation()}>
									<VoiceSampleButton sampleUrl={voice.sampleUrl} name={voice.displayName} />
								</Box>

								<Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
									<Typography variant="body2" noWrap>
										{voice.displayName}
									</Typography>
									{descriptors ? (
										<Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
											{descriptors}
										</Typography>
									) : null}
								</Box>
							</Stack>
						</Card>
					);
				})}

				<Card
					variant="outlined"
					sx={{
						borderColor: askSelected ? "primary.main" : "divider",
						cursor: "pointer",
						gridColumn: { md: "1 / -1" },
						p: 1,
					}}
					onClick={() => {
						setAskSelected(true);
						onChange({ voiceId: null, requestNote: value.requestNote });
					}}
				>
					<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
						<Radio checked={askSelected} size="small" sx={{ p: 0 }} />
						<Typography variant="body2">None of these — ask us for more voices</Typography>
					</Stack>

					{askSelected ? (
						<Box sx={{ mt: 1 }} onClick={(event) => event.stopPropagation()}>
							<TextField
								fullWidth
								multiline
								minRows={2}
								size="small"
								autoFocus
								placeholder="Something younger, or a Malaysian accent…"
								value={value.requestNote}
								onChange={(event) => onChange({ voiceId: null, requestNote: event.target.value })}
								helperText="We'll source more and come back to you. It doesn't hold up your quote."
							/>
						</Box>
					) : null}
				</Card>
			</Box>

			<Box sx={{ textAlign: "right" }}>
				<Button size="small" onClick={() => setOpen(false)}>
					Done
				</Button>
			</Box>
		</Stack>
	);
}
